import { DateFilter } from '@chiroup/core/types/AdminMetrics.type';
import * as Tabs from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDown,
  BarChart2,
  Calendar,
  CheckCircle,
  Clock,
  Headphones,
  HeartPulse,
  Monitor,
  Percent,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { cn } from '../../lib/utils';
import AnalyticsLoading from './components/AnalyticsLoading';
import ChurnByTenureChart from './components/ChurnByTenureChart';
import DashboardNavigation from './components/DashboardNavigation';
import EhrChurnByPhaseChart from './components/EhrChurnByPhaseChart';
import MetricCard from './components/MetricCard';
import ModernDatePicker from './components/ModernDatePicker';
import analyticsService from './services/analytics.service';

// Helper functions
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;

  return num.toLocaleString();
};

const formatPercentage = (num: number): string => {
  return `${num.toFixed(2)}%`;
};

const formatHours = (hours: number): string => {
  if (hours < 1) {
    return `${(hours * 60).toFixed(0)} min`;
  }

  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return `${days}d ${remainingHours.toFixed(0)}h`;
};

const formatDecimal = (num: number): string => {
  return num.toFixed(2);
};

const formatMonths = (months: number): string => {
  if (months < 1) {
    return `${Math.round(months * 30)} days`;
  }

  if (months < 12) {
    return `${months.toFixed(1)} mo`;
  }

  const years = Math.floor(months / 12);
  const remainingMonths = Math.round(months % 12);

  return `${years}y ${remainingMonths}mo`;
};

const getDateValue = (dateString?: string): Date | null => {
  if (!dateString) return null;

  return new Date(dateString);
};

const getCurrentTime = (): string => {
  const now = new Date();

  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const SubscriberDemographics = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'monthToDate',
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [activeSection, setActiveSection] = useState('churn-health');

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'subscriber-health', dateFilter],
    () => analyticsService.getMetrics('subscriber-health', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      keepPreviousData: true,
    }
  );

  // Auto-hide loading after minimum duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumDurationComplete(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, [dateFilter]);

  // Reset minimum duration when filter changes
  useEffect(() => {
    if (isFetching) {
      setMinimumDurationComplete(false);
    }
  }, [isFetching]);

  const allMetrics = data?.metrics || [];

  // Convert metrics array to object for O(1) access instead of O(n)
  const metricsMap = useMemo(() => {
    const map: Record<string, number> = {};
    allMetrics.forEach(metric => {
      if (typeof metric.value === 'number') {
        map[metric.id] = metric.value;
      }
    });

    return map;
  }, [allMetrics]);

  // Get metric value helper with O(1) access
  const getMetricValue = (id: string): number => {
    return metricsMap[id] ?? 0;
  };

  // Show loading
  const shouldShowLoading = isLoading || isFetching || !minimumDurationComplete;

  if (shouldShowLoading) {
    return <AnalyticsLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'past7days', label: 'Past 7 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'past90days', label: 'Past 90 Days' },
    { value: 'yearToDate', label: 'Year to Date' },
    { value: 'custom', label: 'Custom Range' },
  ] as const;

  const handleFilterChange = (type: DateFilter['type']) => {
    if (type === 'custom') {
      setDateFilter({
        type,
        startDate: dateFilter.startDate || new Date().toISOString().split('T')[0],
        endDate: dateFilter.endDate || new Date().toISOString().split('T')[0],
      });
    } else {
      setDateFilter({ type });
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      setDateFilter({
        ...dateFilter,
        [field]: date.toISOString().split('T')[0],
      });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <DashboardNavigation currentDashboardId="subscriber-health" />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Unable to load data
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please try again or contact support
            </p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const essentialsChurnCount = getMetricValue('essentials-churn-count');
  const essentialsChurnRate = getMetricValue('essentials-churn-rate');
  const essentialsRetentionRate = getMetricValue('essentials-retention-rate');
  const ehrLogoChurnCount = getMetricValue('ehr-churn-count');
  const ehrLogoChurnRate = getMetricValue('ehr-churn-rate');
  // Product churn includes both logo churn and downgrades
  const ehrDowngradeCount = getMetricValue('ehr-downgrade-count');
  const ehrProductChurnCount = ehrLogoChurnCount + ehrDowngradeCount;
  const ehrProductChurnRate = getMetricValue('ehr-downgrade-rate');
  const ehrRetentionRate = getMetricValue('ehr-retention-rate');

  // Get churn save metric values
  const essentialsActiveChurnSaves = getMetricValue('essentials-active-churn-saves');
  const essentialsLostChurnSaves = getMetricValue('essentials-lost-churn-saves');
  const essentialsConversionChurnSaves = getMetricValue('essentials-conversion-churn-saves');
  const ehrActiveChurnSaves = getMetricValue('ehr-active-churn-saves');
  const ehrLostChurnSaves = getMetricValue('ehr-lost-churn-saves');
  const ehrConversionChurnSaves = getMetricValue('ehr-conversion-churn-saves');

  // Calculate churn save success rates (only for closed opportunities)
  const essentialsClosedChurnSaves = essentialsLostChurnSaves + essentialsConversionChurnSaves;
  const essentialsChurnSaveSuccessRate =
    essentialsClosedChurnSaves > 0
      ? (essentialsConversionChurnSaves / essentialsClosedChurnSaves) * 100
      : 0;

  const ehrClosedChurnSaves = ehrLostChurnSaves + ehrConversionChurnSaves;
  const ehrChurnSaveSuccessRate =
    ehrClosedChurnSaves > 0 ? (ehrConversionChurnSaves / ehrClosedChurnSaves) * 100 : 0;

  // Get health score metrics
  const essentialsPostBoardingMetric = allMetrics.find(
    m => m.id === 'essentials-health-post-boarding'
  );
  const ehrPostBoardingMetric = allMetrics.find(m => m.id === 'ehr-health-post-boarding');
  const essentialsHealthMetric = allMetrics.find(m => m.id === 'essentials-health-onboarding');
  const ehrHealthMetric = allMetrics.find(m => m.id === 'ehr-onboarding-health-score');

  // Get support metrics
  const essentialsFirstTouchRate = getMetricValue('essentials-first-touch-resolution-rate');
  const ehrFirstTouchRate = getMetricValue('ehr-first-touch-resolution-rate');
  const essentialsAvgResolutionTime = getMetricValue('essentials-avg-resolution-time');
  const ehrAvgResolutionTime = getMetricValue('ehr-avg-resolution-time');
  const essentialsAvgCasesPerAccount = getMetricValue('essentials-avg-cases-per-account');
  const ehrAvgCasesPerAccount = getMetricValue('ehr-avg-cases-per-account');

  // Get NPS metrics
  const essentialsNPSMetric = allMetrics?.find(m => m.id === 'essentials-nps-score');
  const ehrNPSMetric = allMetrics?.find(m => m.id === 'ehr-nps-score');

  // Get lifecycle metrics
  const essentialsActiveAvgMonths = getMetricValue('essentials-active-avg-months');
  const essentialsChurnedAvgMonths = getMetricValue('essentials-churned-avg-months');
  const ehrActiveAvgMonths = getMetricValue('ehr-active-avg-months');
  const ehrChurnedAvgMonths = getMetricValue('ehr-churned-avg-months');
  const essentialsToEhrExpansionCount = getMetricValue('essentials-to-ehr-expansion-count');
  const essentialsToEhrExpansionRate = getMetricValue('essentials-to-ehr-expansion-rate');

  // Get churn by tenure visualization data
  const churnByTenureMetric = allMetrics?.find(m => m.id === 'essentials-churn-by-tenure');
  const churnByTenureData = churnByTenureMetric?.value as Record<string, number> | undefined;

  // Get EHR churn by phase visualization data
  const ehrChurnByPhaseMetric = allMetrics?.find(m => m.id === 'ehr-churn-by-phase');
  const ehrChurnByPhaseData = ehrChurnByPhaseMetric?.value as
    | {
        phases: Record<string, number>;
        records: any[];
      }
    | undefined;

  return (
    <div className="relative">
      <DashboardNavigation currentDashboardId="subscriber-health" />

      <div className="p-6">
        {/* Enhanced Filter Section */}
        <div className="mb-8">
          <motion.div
            className="bg-gradient-to-r from-green-600 via-emerald-700 to-teal-700 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Title Section */}
              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {/* Title and Icon Row */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Subscriber Health Metrics</h2>
                    <p className="text-cyan-100 text-sm">Monitor subscriber health and churn</p>
                  </div>
                </div>

                {/* Live Data Indicator */}
                <motion.div
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 self-start ml-14"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  {/* Animated Pulse Dot */}
                  <div className="relative flex items-center">
                    <motion.div
                      className="w-2 h-2 bg-cyan-400 rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Ripple Effect */}
                    <motion.div
                      className="absolute inset-0 bg-cyan-400 rounded-full"
                      animate={{
                        scale: [1, 2.5],
                        opacity: [0.6, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  </div>

                  {/* Live Data Text */}
                  <div className="flex items-center">
                    <motion.span
                      className="text-xs font-bold tracking-wider text-white drop-shadow-sm"
                      animate={{ opacity: [0.9, 1, 0.9] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      LIVE DATA
                    </motion.span>
                    <span className="text-white/80 mx-1 text-xs">•</span>
                    <motion.span
                      className="text-xs font-medium text-white/90 tracking-wide"
                      key={getCurrentTime()}
                      initial={{ opacity: 0, y: 2 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      Updated {getCurrentTime()}
                    </motion.span>
                  </div>
                </motion.div>
              </motion.div>

              {/* Enhanced Date Range Selector */}
              <motion.div
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {dateOptions.map(option => (
                    <motion.button
                      key={option.value}
                      onClick={() => handleFilterChange(option.value)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap',
                        'border focus:outline-none focus:ring-2 focus:ring-cyan-400',
                        dateFilter.type === option.value
                          ? 'bg-white text-cyan-700 hover:bg-white/95 shadow-md border-cyan-200'
                          : 'text-white hover:bg-white/20 hover:text-cyan-100 border-white/20'
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {option.label}
                    </motion.button>
                  ))}
                </div>

                {/* Advanced Custom Date Pickers */}
                <AnimatePresence>
                  {dateFilter.type === 'custom' && (
                    <motion.div
                      className="mt-4 pt-4 border-t border-white/20"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/90 font-medium">Start Date</span>
                          <ModernDatePicker
                            selected={getDateValue(dateFilter.startDate)}
                            onChange={date => handleDateChange('startDate', date)}
                            placeholder="MM/DD/YYYY"
                            maxDate={new Date()}
                            align="start"
                            calendarType="start-date"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/90 font-medium">End Date</span>
                          <ModernDatePicker
                            selected={getDateValue(dateFilter.endDate)}
                            onChange={date => handleDateChange('endDate', date)}
                            placeholder="MM/DD/YYYY"
                            minDate={getDateValue(dateFilter.startDate) || undefined}
                            maxDate={new Date()}
                            align="start"
                            calendarType="end-date"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Section Navigation Tabs */}
        <Tabs.Root value={activeSection} onValueChange={setActiveSection}>
          <Tabs.List className="grid w-full grid-cols-1 sm:grid-cols-3 gap-3 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl max-w-4xl mx-auto mb-8">
            <Tabs.Trigger
              value="churn-health"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-green-500 data-[state=active]:via-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <HeartPulse className="h-4 w-4" />
                <span>Churn & Health Metrics</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="support-lifecycle"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:via-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Headphones className="h-4 w-4" />
                <span>Support & Lifecycle Metrics</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="visualizations"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:via-orange-500 data-[state=active]:to-yellow-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <BarChart2 className="h-4 w-4" />
                <span>Visualizations</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>

          {/* Churn & Health Metrics Tab Content */}
          <Tabs.Content value="churn-health" className="focus:outline-none">
            {/* Churned Subscribers Count - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-red-50/30 to-orange-50/30 dark:from-gray-800/50 dark:via-red-900/20 dark:to-orange-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-red-600 via-orange-600 to-red-700 bg-clip-text text-transparent flex items-center gap-3">
                  <TrendingDown className="w-8 h-8 text-red-600" />
                  Churned Subscribers
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Churn Count */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <TrendingDown className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(essentialsChurnCount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Essentials subscribers who churned
                  </p>
                </motion.div>

                {/* EHR Logo Churn Count */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Monitor className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR Logo Churn
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(ehrLogoChurnCount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Subscribers who canceled all products
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Churn Rate - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-orange-50/30 to-red-50/30 dark:from-gray-800/50 dark:via-orange-900/20 dark:to-red-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-orange-600 via-red-600 to-orange-700 bg-clip-text text-transparent flex items-center gap-3">
                  <Percent className="w-8 h-8 text-orange-600" />
                  Churn Rate (Monthly)
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Churn Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(essentialsChurnRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Monthly churn rate (projected)
                  </p>
                </motion.div>

                {/* EHR Logo Churn Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR Logo Churn
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(ehrLogoChurnRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Monthly logo churn rate (projected)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Retention Rate - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 dark:from-gray-800/50 dark:via-green-900/20 dark:to-emerald-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 bg-clip-text text-transparent flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  Retention Rate (Monthly)
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Retention Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(essentialsRetentionRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Monthly retention rate (100 - Churn Rate)
                  </p>
                </motion.div>

                {/* EHR Retention Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(ehrRetentionRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Monthly retention rate (100 - Logo - Product)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* EHR Product Churn - Standalone Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/30 dark:from-gray-800/50 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 bg-clip-text text-transparent flex items-center gap-3">
                  <ArrowDown className="w-8 h-8 text-purple-600" />
                  EHR Product Churn (Downgrade)
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* EHR Product Churn Count */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <ArrowDown className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Product Churn Count
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(ehrProductChurnCount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Subscribers who dropped EHR product
                  </p>
                </motion.div>

                {/* EHR Product Churn Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Product Churn Rate
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(ehrProductChurnRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Monthly product churn rate (projected)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Active Churn Saves - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-amber-50/30 to-yellow-50/30 dark:from-gray-800/50 dark:via-amber-900/20 dark:to-yellow-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.9, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 via-yellow-600 to-amber-700 bg-clip-text text-transparent flex items-center gap-3">
                  <Clock className="w-8 h-8 text-amber-600" />
                  Active Churn Saves
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Active Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.95 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(essentialsActiveChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves actively being worked (all time)
                  </p>
                </motion.div>

                {/* EHR Active Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Clock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(ehrActiveChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves actively being worked (all time)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Lost Churn Saves - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-red-50/30 to-pink-50/30 dark:from-gray-800/50 dark:via-red-900/20 dark:to-pink-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.1, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.15 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-red-600 via-pink-600 to-red-700 bg-clip-text text-transparent flex items-center gap-3">
                  <TrendingDown className="w-8 h-8 text-red-600" />
                  Lost Churn Saves
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Lost Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.15 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <TrendingDown className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(essentialsLostChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves that were lost (all time)
                  </p>
                </motion.div>

                {/* EHR Lost Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <TrendingDown className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(ehrLostChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves that were lost (all time)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Successful Churn Saves - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-green-50/30 to-teal-50/30 dark:from-gray-800/50 dark:via-green-900/20 dark:to-teal-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.3, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.35 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-teal-600 to-green-700 bg-clip-text text-transparent flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  Successful Churn Saves
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Successful Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.35 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(essentialsConversionChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves successfully converted (all time)
                  </p>
                </motion.div>

                {/* EHR Successful Churn Saves */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(ehrConversionChurnSaves)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Churn saves successfully converted (all time)
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Churn Save Success Rate - Side by Side Comparison */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.5, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.55 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 bg-clip-text text-transparent flex items-center gap-3">
                  <Percent className="w-8 h-8 text-cyan-600" />
                  Churn Save Success Rate
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Success Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.55 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(essentialsChurnSaveSuccessRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Successful / (Lost + Successful) — closed only
                  </p>
                </motion.div>

                {/* EHR Success Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(ehrChurnSaveSuccessRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Successful / (Lost + Successful) — closed only
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Post-Boarding Health Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 dark:from-gray-800/50 dark:via-green-900/20 dark:to-emerald-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.6, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-700 bg-clip-text text-transparent flex items-center gap-3">
                  <HeartPulse className="w-8 h-8 text-green-600" />
                  Post-Boarding Health Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Post-Boarding Health Score */}
                {(() => {
                  if (
                    !essentialsPostBoardingMetric ||
                    !essentialsPostBoardingMetric.value ||
                    typeof essentialsPostBoardingMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = essentialsPostBoardingMetric.value as Record<string, number>;
                  const green = data.green || data.Green || 0;
                  const yellow = data.yellow || data.Yellow || 0;
                  const red = data.red || data.Red || 0;
                  const total = green + yellow + red;
                  const healthyPercentage =
                    total > 0 ? (((green + yellow) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                            <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              Essentials Post-Boarding Health Score
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                              {healthyPercentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ({green + yellow} healthy / {total} total) - 90+ days
                      </p>
                    </motion.div>
                  );
                })()}

                {/* EHR Post-Boarding Health Score */}
                {(() => {
                  if (
                    !ehrPostBoardingMetric ||
                    !ehrPostBoardingMetric.value ||
                    typeof ehrPostBoardingMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = ehrPostBoardingMetric.value as Record<string, number>;
                  const healthy = data.healthy || 0;
                  const atRisk = data.atRisk || 0;
                  const unhealthy = data.unhealthy || 0;
                  const total = data.total || healthy + atRisk + unhealthy;
                  const healthyPercentage =
                    total > 0 ? (((healthy + atRisk) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.85 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                            <Monitor className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              EHR Post-Boarding Health Score
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                              {healthyPercentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ({healthy + atRisk} healthy / {total} total) - Closed Won
                      </p>
                    </motion.div>
                  );
                })()}
              </div>
            </motion.div>

            {/* Onboarding Health Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-teal-50/30 to-cyan-50/30 dark:from-gray-800/50 dark:via-teal-900/20 dark:to-cyan-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.8, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-700 bg-clip-text text-transparent flex items-center gap-3">
                  <HeartPulse className="w-8 h-8 text-teal-600" />
                  Onboarding Health Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Essentials Onboarding Health Score */}
                {(() => {
                  if (
                    !essentialsHealthMetric ||
                    !essentialsHealthMetric.value ||
                    typeof essentialsHealthMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = essentialsHealthMetric.value as Record<string, number>;
                  const green = data.green || data.Green || 0;
                  const yellow = data.yellow || data.Yellow || 0;
                  const red = data.red || data.Red || 0;
                  const total = green + yellow + red;
                  const healthyPercentage =
                    total > 0 ? (((green + yellow) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-blue-500 dark:border-l-blue-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                            <CheckCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              Essentials Onboarding Health Score
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                              {healthyPercentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ({green + yellow} healthy / {total} total) - first 90 days
                      </p>
                    </motion.div>
                  );
                })()}

                {/* EHR Onboarding Health Score */}
                {(() => {
                  if (
                    !ehrHealthMetric ||
                    !ehrHealthMetric.value ||
                    typeof ehrHealthMetric.value !== 'object'
                  ) {
                    return null;
                  }

                  const data = ehrHealthMetric.value as Record<string, number>;
                  const healthy = data.healthy || 0;
                  const atRisk = data.atRisk || 0;
                  const unhealthy = data.unhealthy || 0;
                  const total = data.total || healthy + atRisk + unhealthy;
                  const healthyPercentage =
                    total > 0 ? (((healthy + atRisk) / total) * 100).toFixed(1) : '0.0';

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 }}
                      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-l-4 border-l-purple-500 dark:border-l-purple-400 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                            <CheckCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              EHR Onboarding Health Score
                            </h3>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                              {healthyPercentage}%
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        ({healthy + atRisk} healthy / {total} total)
                      </p>
                    </motion.div>
                  );
                })()}
              </div>
            </motion.div>
          </Tabs.Content>

          {/* Support & Lifecycle Metrics Tab Content */}
          <Tabs.Content value="support-lifecycle" className="focus:outline-none">
            {/* Essentials Support Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-green-50/30 to-emerald-50/30 dark:from-gray-800/50 dark:via-green-900/20 dark:to-emerald-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-3">
                  <Headphones className="w-8 h-8 text-green-600" />
                  Essentials Support Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Essentials First Touch Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials First Touch Rate
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(essentialsFirstTouchRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Cases resolved on first response (&lt; 20 min)
                  </p>
                </motion.div>

                {/* Essentials Avg Resolution Time */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                        <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials Avg TTR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatHours(essentialsAvgResolutionTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average time to resolution
                  </p>
                </motion.div>

                {/* Essentials Avg Cases per Account */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl">
                        <BarChart2 className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials Avg Cases/Account
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatDecimal(essentialsAvgCasesPerAccount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Per account with cases</p>
                </motion.div>
              </div>
            </motion.div>

            {/* EHR Support Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-cyan-50/30 to-blue-50/30 dark:from-gray-800/50 dark:via-cyan-900/20 dark:to-blue-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.7, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center gap-3">
                  <Headphones className="w-8 h-8 text-cyan-600" />
                  EHR Support Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* EHR First Touch Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl">
                        <CheckCircle className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR First Touch Rate
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(ehrFirstTouchRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Cases resolved on first response (&lt; 20 min)
                  </p>
                </motion.div>

                {/* EHR Avg Resolution Time */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                        <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR Avg TTR
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatHours(ehrAvgResolutionTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average time to resolution
                  </p>
                </motion.div>

                {/* EHR Avg Cases per Account */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.2 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl">
                        <BarChart2 className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          EHR Avg Cases/Account
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatDecimal(ehrAvgCasesPerAccount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Per account with cases</p>
                </motion.div>
              </div>
            </motion.div>

            {/* NPS Scores Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30 dark:from-gray-800/50 dark:via-emerald-900/20 dark:to-teal-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.9, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent flex items-center gap-3">
                  <Headphones className="w-8 h-8 text-emerald-600" />
                  Net Promoter Score (NPS)
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 gap-6">
                {/* Essentials NPS */}
                {essentialsNPSMetric && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.0 }}
                  >
                    <MetricCard metric={essentialsNPSMetric} />
                  </motion.div>
                )}

                {/* EHR NPS */}
                {ehrNPSMetric && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1 }}
                  >
                    <MetricCard metric={ehrNPSMetric} />
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Essentials Lifecycle Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-purple-50/30 to-violet-50/30 dark:from-gray-800/50 dark:via-purple-900/20 dark:to-violet-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.1, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700 bg-clip-text text-transparent flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-purple-600" />
                  Essentials Lifecycle Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Active Essentials Avg Months */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.6 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                        <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Active Subscribers Avg Tenure
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatMonths(essentialsActiveAvgMonths)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average months since subscription start
                  </p>
                </motion.div>

                {/* Churned Essentials Avg Months */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.7 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                        <Calendar className="h-6 w-6 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Churned Subscribers Avg Lifetime
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatMonths(essentialsChurnedAvgMonths)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average months from start to end date
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* EHR Lifecycle Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/30 dark:from-gray-800/50 dark:via-blue-900/20 dark:to-indigo-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.2, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent flex items-center gap-3">
                  <Calendar className="w-8 h-8 text-blue-600" />
                  EHR Lifecycle Metrics
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Active EHR Avg Months */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.8 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                        <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Active EHR Subscribers Avg Tenure
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatMonths(ehrActiveAvgMonths)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average months since EHR go-live date
                  </p>
                </motion.div>

                {/* Churned EHR Avg Months */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.9 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                        <Calendar className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Churned EHR Subscribers Avg Lifetime
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatMonths(ehrChurnedAvgMonths)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Average months from go-live date to end date
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Essentials to EHR Expansion Metrics Section */}
            <motion.div
              className="relative bg-gradient-to-br from-white via-green-50/30 to-lime-50/30 dark:from-gray-800/50 dark:via-green-900/20 dark:to-lime-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 1.4, type: 'spring', stiffness: 80 }}
              whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5 }}
                className="mb-6"
              >
                <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-lime-600 to-emerald-700 bg-clip-text text-transparent flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                  Essentials to EHR Expansion
                </h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Expansion Count */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.0 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                        <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials to EHR Expansions
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatNumber(essentialsToEhrExpansionCount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Active EHR accounts that expanded from Essentials
                  </p>
                </motion.div>

                {/* Expansion Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                        <Percent className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Essentials to EHR Expansion Rate
                        </h3>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                          {formatPercentage(essentialsToEhrExpansionRate)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Percentage of EHR accounts from Essentials
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </Tabs.Content>

          {/* Visualizations Tab Content */}
          <Tabs.Content value="visualizations" className="focus:outline-none">
            <div className="space-y-8">
              {/* Essentials Churn by Tenure Chart */}
              {churnByTenureData ? (
                <ChurnByTenureChart
                  data={churnByTenureData}
                  title="Essentials Churn by Customer Tenure"
                  isLoading={isFetching}
                />
              ) : (
                <motion.div
                  className="relative bg-gradient-to-br from-white via-violet-50/30 to-fuchsia-50/30 dark:from-gray-800/50 dark:via-violet-900/20 dark:to-fuchsia-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 80 }}
                  whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mb-6"
                  >
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent flex items-center gap-3">
                      <BarChart2 className="w-8 h-8 text-violet-600" />
                      Loading Visualization
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                      Fetching Essentials churn data...
                    </p>
                  </motion.div>
                </motion.div>
              )}

              {/* EHR Churn by Phase Chart */}
              {ehrChurnByPhaseData ? (
                <EhrChurnByPhaseChart
                  data={ehrChurnByPhaseData}
                  title="EHR Churn by Phase (All Time)"
                  isLoading={isFetching}
                />
              ) : (
                <motion.div
                  className="relative bg-gradient-to-br from-white via-purple-50/30 to-indigo-50/30 dark:from-gray-800/50 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm mb-8"
                  initial={{ opacity: 0, y: 40, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 80 }}
                  whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mb-6"
                  >
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent flex items-center gap-3">
                      <BarChart2 className="w-8 h-8 text-purple-600" />
                      Loading Visualization
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                      Fetching EHR churn data...
                    </p>
                  </motion.div>
                </motion.div>
              )}
            </div>
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default SubscriberDemographics;
